/** The Studio conversation thread (PRD §30.1): studio text, owner messages, collapsed activity, inline cards. */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "../../app/router";
import type { Message, SeriesDetail } from "../../api/types";
import { Icon } from "../../components/Icon";
import { Badge } from "../../components/ui";
import { dateTime, relative } from "../../lib/format";
import { DecisionCard } from "./DecisionCard";
import { PlanApprovalCard } from "./PlanApprovalCard";

export type PendingMessage = { key: string; body: string; failed?: boolean };
type Item = { kind: "message"; m: Message } | { kind: "activity"; items: Message[]; sealed?: boolean } | { kind: "auto"; m: Message };
/** A learning the Studio just proposed needs the owner: it stays on its own line instead of folding into "earlier steps". */
const isProposedLearning = (m: Message) => m.detail?.type === "learning" && (m.detail.action === undefined || m.detail.action === "proposed");

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined);
/** A decision the Studio took on its own (autonomy "auto"): the runner's log {kind:"auto_decision"}, relayed as an activity with its payload in `detail`. */
export function autoDecisionOf(m: Message): { question?: string; chosen?: string; why?: string } | null {
  const d = m.detail;
  if (!d || (d.type !== "auto_decision" && d.kind !== "auto_decision")) return null;
  return { question: str(d.question), chosen: str(d.chosen), why: str(d.why) };
}

/** Collapses consecutive activity lines into one group (latest shown, earlier ones expandable). Auto-decisions stay visible on their own line. */
export function groupThread(messages: Message[]): Item[] {
  const out: Item[] = [];
  for (const m of messages) {
    const last = out[out.length - 1];
    if (autoDecisionOf(m)) out.push({ kind: "auto", m });
    else if (m.kind === "activity" && isProposedLearning(m)) out.push({ kind: "activity", items: [m], sealed: true });
    else if (m.kind === "activity") {
      if (last?.kind === "activity" && !last.sealed) last.items.push(m);
      else out.push({ kind: "activity", items: [m] });
    } else out.push({ kind: "message", m });
  }
  return out;
}

export function Conversation({ detail, messages, pending }: { detail: SeriesDetail; messages: Message[]; pending: PendingMessage[] }) {
  const endRef = useRef<HTMLDivElement>(null);
  const count = messages.length + pending.length;
  const prev = useRef(0);
  const [unseen, setUnseen] = useState(false);

  useLayoutEffect(() => {
    const first = prev.current === 0;
    const grew = count > prev.current;
    prev.current = count;
    if (!grew) return;
    const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 320;
    if (first || nearBottom || pending.length) endRef.current?.scrollIntoView?.({ block: "end", behavior: first ? "auto" : "smooth" });
    else setUnseen(true);
  }, [count, pending.length]);
  useEffect(() => {
    const onScroll = () => { if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 120) setUnseen(false); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = groupThread(messages);
  return (
    <div className="thread" role="log" aria-live="polite" aria-relevant="additions" aria-label="Studio conversation">
      {items.length === 0 && pending.length === 0 && <p className="thread-empty muted">The conversation starts when the Studio picks up this series.</p>}
      {items.map((it, i) => it.kind === "activity"
        ? <ActivityGroup key={`a-${it.items[0]!.id}`} items={it.items} live={i === items.length - 1 && detail.session.state === "active"} />
        : it.kind === "auto" ? <AutoDecisionLine key={it.m.id} m={it.m} />
        : <MessageView key={it.m.id} m={it.m} detail={detail} />)}
      {pending.map((p) => (
        <div key={p.key} className="msg msg-owner">
          <div className="bubble">{p.body}</div>
          <span className={`msg-meta ${p.failed ? "danger-text" : ""}`}>{p.failed ? "Not sent" : "Sending…"}</span>
        </div>
      ))}
      <div ref={endRef} className="thread-end" />
      {unseen && <button type="button" className="new-pill" onClick={() => { endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" }); setUnseen(false); }}><Icon name="chevronDown" size={18} /> New messages</button>}
    </div>
  );
}

/**
 * A Studio message renders as a Studio bubble whether it came from a work item or not (M1.5: the runner posts free-standing
 * replies between work items, with no work item and kind "text"). A card message without a card payload falls back to text.
 */
function MessageView({ m, detail }: { m: Message; detail: SeriesDetail }) {
  if (m.kind === "card" && m.card) return <div className="msg msg-card"><CardView m={m} detail={detail} /></div>;
  if (!m.body) return null;
  if (m.direction === "system") return <p className="msg-system"><span>{m.body}</span> <time dateTime={m.created_at}>{relative(m.created_at)}</time></p>;
  const owner = m.direction === "owner";
  return (
    <div className={`msg ${owner ? "msg-owner" : "msg-studio"}`}>
      {!owner && <span className="msg-author"><Icon name="sparkle" size={14} /> Studio</span>}
      <div className="bubble">{m.body}</div>
      <span className="msg-meta">
        <time dateTime={m.created_at} title={dateTime(m.created_at)}>{relative(m.created_at)}</time>
        {owner && (m.delivered_at ? " · Delivered" : " · Queued for the Studio's next turn")}
      </span>
    </div>
  );
}

function AutoDecisionLine({ m }: { m: Message }) {
  const a = autoDecisionOf(m)!;
  const text = a.question && a.chosen ? `${a.question} → ${a.chosen}` : a.chosen ?? m.body?.replace(/^Auto-decided:\s*/i, "") ?? "";
  return (
    <div className="auto-line">
      <Icon name="check" size={16} />
      <div className="auto-line-body">
        <span><b>Auto-decided:</b> {text} <time className="muted" dateTime={m.created_at} title={dateTime(m.created_at)}>{relative(m.created_at)}</time></span>
        {a.why && <span className="muted small">{a.why}</span>}
      </div>
    </div>
  );
}

/** An activity line; a proposed learning links to where the owner decides it. */
function ActivityText({ m }: { m: Message }) {
  return <>{m.body}{isProposedLearning(m) && <Link to="/learnings" className="activity-link">Review</Link>}</>;
}

function ActivityGroup({ items, live }: { items: Message[]; live: boolean }) {
  const [open, setOpen] = useState(false);
  const latest = items[items.length - 1]!;
  const earlier = items.length - 1;
  return (
    <div className="activity">
      <span className={`activity-dot ${live ? "is-live" : ""}`} aria-hidden />
      <div className="activity-body">
        {open && items.slice(0, -1).map((a) => <p key={a.id} className="activity-line muted"><ActivityText m={a} /> <time dateTime={a.created_at}>{relative(a.created_at)}</time></p>)}
        <p className="activity-line"><ActivityText m={latest} /> <time className="muted" dateTime={latest.created_at}>{relative(latest.created_at)}</time></p>
        {earlier > 0 && <button type="button" className="text-link small" aria-expanded={open} onClick={() => setOpen((x) => !x)}>{open ? "Hide earlier steps" : `Show ${earlier} earlier step${earlier > 1 ? "s" : ""}`}</button>}
      </div>
    </div>
  );
}

function CardView({ m, detail }: { m: Message; detail: SeriesDetail }) {
  const card = m.card!;
  if (card.type === "plan") {
    if (detail.plan && String(detail.plan.version) === card.ref) return <PlanApprovalCard seriesId={detail.series.id} record={detail.plan} seriesStatus={detail.series.status} />;
    return <div className="plan-card plan-card-done"><Icon name="check" /> <span>{card.title ?? `Plan v${card.ref}`} — superseded by a newer plan</span></div>;
  }
  if (card.type === "review") {
    const ref = detail.episodes.flatMap((e) => e.versions.map((v) => ({ e, v }))).find((x) => x.v.id === card.ref);
    const status = ref?.v.status;
    return (
      <Link to={`/review/${card.ref}`} className="review-card">
        <span className="review-card-icon"><Icon name="play" /></span>
        <span className="review-card-main">
          <span className="eyebrow">Ready for review</span>
          <span className="review-card-title">{card.title ?? (ref ? `Episode ${ref.e.sequence} v${ref.v.version}` : "New version")}{ref ? ` · ${ref.e.title}` : ""}</span>
          <span className="review-card-meta">
            {card.score != null && <Badge tone={card.score >= 9 ? "ok" : "attention"}>Reviewer {card.score.toFixed(1)}</Badge>}
            {status && status !== "in_review" && <Badge tone={status === "approved" ? "ok" : "neutral"}>{status.replace(/_/g, " ")}</Badge>}
          </span>
        </span>
        <Icon name="chevronRight" />
      </Link>
    );
  }
  const d = detail.decisions.find((x) => x.id === card.ref);
  if (!d) return <p className="msg-system">A decision was asked here (no longer available).</p>;
  return <DecisionCard decision={d} />;
}
